import * as Y from 'yjs';
import { createCommands, type Commands } from './commands';
import { LOCAL_ORIGIN, REPAIR_ORIGIN } from './origin';
import { repairGeometry } from './normalize';
import { parseObject, SCHEMA_VERSION, type ObjectData } from './schema';
import { readObject } from './ytypes';
import { buildGraph, type SceneGraph } from './graph';
import { readMeta, type BoardMeta } from './meta';
import { boardCounts, type BoardCounts, type LimitKind } from './limits';
import { pageAssignment, readPages, type PageEntry } from './pages';

export { LOCAL_ORIGIN, REPAIR_ORIGIN };

export function createBoardDoc(): Y.Doc {
  const doc = new Y.Doc();
  doc.getMap('objects');
  const meta = doc.getMap('meta');
  meta.set('schemaVersion', SCHEMA_VERSION);
  meta.set('epoch', 0);
  return doc;
}

function deepFreeze<T>(v: T): T {
  if (v && typeof v === 'object' && !Object.isFrozen(v)) {
    Object.freeze(v);
    for (const child of Object.values(v as object)) deepFreeze(child);
  }
  return v;
}

type Listener = () => void;

/**
 * Immutable per-object snapshots over the Yjs document, with fine-grained subscriptions.
 * The only module that writes to Yjs is ./commands (via `store.commands`).
 * One page is active (P14): `list()` and `graph()` are that page's; `listAll()` and `listPage()` read further.
 */
export class SceneStore {
  readonly commands: Commands;
  private readonly objects: Y.Map<Y.Map<unknown>>;
  private readonly pagesMap: Y.Map<unknown>;
  private readonly cache = new Map<string, Readonly<ObjectData>>();
  private sorted: ReadonlyArray<Readonly<ObjectData>> | null = null;
  private readonly byPage = new Map<string, ReadonlyArray<Readonly<ObjectData>>>();
  private readonly graphs = new Map<string, SceneGraph>();
  private pagesCache: readonly PageEntry[] | null = null;
  private assignment: Map<string, string> | null = null;
  private activePage: string;
  private readonly pageSubs = new Set<Listener>();
  private readonly pagesSubs = new Set<Listener>();
  private readonly membershipSubs = new Set<Listener>();
  private readonly idSubs = new Map<string, Set<Listener>>();
  private readonly listSubs = new Set<Listener>();
  private readonly histSubs = new Set<Listener>();
  private readonly limitSubs = new Set<(kind: LimitKind) => void>();
  private readonly readOnlySubs = new Set<Listener>();
  private readonly metaSubs = new Set<Listener>();
  private metaCache: BoardMeta | null = null;
  private readonly history: Y.UndoManager;
  private readOnlyValue = false;
  /** Open gesture: undo-stack depth at its start and the capture timeout to restore. */
  private gesture: { depth: number; captureTimeout: number } | null = null;
  private author: (() => string | null) | null;
  private onObjects!: (events: Y.YEvent<Y.AbstractType<unknown>>[]) => void;
  private onPages!: () => void;
  private onMeta!: () => void;

  /**
   * `author`: the signed-in user's id, recorded on every object this store adds (P5d); absent or null in local mode.
   * `now`: the clock of `createdAt` (tests).
   */
  constructor(readonly doc: Y.Doc, opts: { captureTimeout?: number; author?: () => string | null; now?: () => number } = {}) {
    this.objects = doc.getMap('objects') as Y.Map<Y.Map<unknown>>;
    this.pagesMap = doc.getMap('pages');
    this.author = opts.author ?? null;
    for (const id of this.objects.keys()) this.load(id);
    this.activePage = this.pages()[0]!.id;

    this.objects.observeDeep((this.onObjects = (events) => {
      const changed = new Set<string>();
      // Edits inside a nested type (rich text, table cells) leave ids, order and geometry alone: no list notification,
      // so typing never rebuilds the spatial index.
      let structural = false;
      // Pages follow roots (P14 Ruling 5): only an added or removed object, a parent or a page change moves one.
      let membership = false;
      for (const e of events) {
        if (e.target === this.objects) {
          for (const k of (e as Y.YMapEvent<Y.Map<unknown>>).keysChanged) changed.add(k);
          structural = true;
          membership = true;
        } else if (typeof e.path[0] === 'string') {
          changed.add(e.path[0]);
          if (e.path.length < 2) {
            structural = true;
            const keys = (e as Y.YMapEvent<unknown>).keysChanged;
            if (keys?.has('parentId') || keys?.has('pageId')) membership = true;
          }
        }
      }
      for (const id of changed) this.load(id);
      this.invalidate(membership);
      for (const id of changed) this.idSubs.get(id)?.forEach((cb) => cb());
      if (structural) this.listSubs.forEach((cb) => cb());
      if (membership) this.membershipSubs.forEach((cb) => cb());
    }));

    this.pagesMap.observeDeep((this.onPages = () => {
      this.pagesCache = null;
      this.invalidate(true);
      // A page removed by a peer, an undo or a restore: the first page takes over.
      const moved = !this.pages().some((p) => p.id === this.activePage);
      if (moved) this.activePage = this.pages()[0]!.id;
      this.pagesSubs.forEach((cb) => cb());
      if (moved) this.pageSubs.forEach((cb) => cb());
      this.listSubs.forEach((cb) => cb());
      this.membershipSubs.forEach((cb) => cb());
    }));

    // Pages are undoable like objects (P14 Ruling 3).
    this.history = new Y.UndoManager([this.objects, this.pagesMap], {
      trackedOrigins: new Set([LOCAL_ORIGIN]),
      captureTimeout: opts.captureTimeout ?? 500,
    });
    for (const ev of ['stack-item-added', 'stack-item-popped', 'stack-cleared'] as const) {
      this.history.on(ev, () => this.histSubs.forEach((cb) => cb()));
    }

    const metaMap = doc.getMap('meta');
    metaMap.observe((this.onMeta = () => {
      this.metaCache = null;
      this.metaSubs.forEach((cb) => cb());
    }));

    this.commands = createCommands({
      doc,
      objects: this.objects,
      list: () => this.list(),
      listAll: () => this.listAll(),
      page: () => this.activePage,
      listPage: (pageId) => this.listPage(pageId),
      graphOf: (pageId) => this.graphOf(pageId),
      pageOf: (id) => this.pageOf(id),
      graph: () => this.graph(),
      read: (id) => this.getObject(id),
      readOnly: () => this.readOnlyValue,
      // Each command is its own undo step; only gestures and typing bursts (y-tiptap, not commands) merge writes.
      step: () => {
        if (!this.gesture && !this.doc._transaction) this.history.stopCapturing();
      },
      limited: (kind) => this.limitSubs.forEach((cb) => cb(kind)),
      stamp: () => {
        const createdBy = this.author?.();
        return createdBy ? { createdBy, createdAt: Math.max(0, Math.round((opts.now ?? Date.now)())) } : null;
      },
    });
  }

  private load(id: string): void {
    const m = this.objects.get(id);
    const parsed = m ? parseObject(readObject(m)) : null;
    // Read-side repair: whatever a peer or an import wrote, the snapshot is visible and selectable.
    if (parsed) this.cache.set(id, deepFreeze(repairGeometry(parsed)));
    else this.cache.delete(id); // invalid or deleted: hidden, never a crash
  }

  /**
   * Stops following the document (a second, read-only view of it: a page link's miniature, P14). The document and
   * every other store on it are untouched; this store's snapshots are emptied.
   */
  destroy(): void {
    this.objects.unobserveDeep(this.onObjects);
    this.pagesMap.unobserveDeep(this.onPages);
    this.doc.getMap('meta').unobserve(this.onMeta);
    this.history.destroy();
    this.cache.clear();
    this.invalidate(true);
    for (const set of [this.idSubs, this.listSubs, this.histSubs, this.readOnlySubs, this.metaSubs, this.pageSubs, this.pagesSubs, this.membershipSubs]) set.clear();
  }

  /** The rich-text fragment of a text, sticky or shape (null before its first edit on a pre-v2 object). */
  fragment(id: string): Y.XmlFragment | null {
    const f = this.objects.get(id)?.get('content');
    return f instanceof Y.XmlFragment ? f : null;
  }

  /** The board's undo manager, shared with the rich-text editor (one undo stack for the whole board). */
  get undoManager(): Y.UndoManager {
    return this.history;
  }

  /** Also records transactions of this origin as undo steps (the rich-text binding's own origin). */
  trackOrigin(origin: unknown): void {
    this.history.trackedOrigins.add(origin);
  }

  getObject(id: string): Readonly<ObjectData> | undefined {
    return this.cache.get(id);
  }

  private invalidate(membership: boolean): void {
    this.sorted = null;
    this.byPage.clear();
    this.graphs.clear();
    if (membership) this.assignment = null;
  }

  /** Every valid object of every page, ascending z (stable reference until something changes). */
  listAll(): ReadonlyArray<Readonly<ObjectData>> {
    this.sorted ??= [...this.cache.values()].sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : a.id < b.id ? -1 : 1));
    return this.sorted;
  }

  /** The active page's valid objects, ascending z (stable reference until something changes). */
  list(): ReadonlyArray<Readonly<ObjectData>> {
    return this.listPage(this.activePage);
  }

  /** One page's valid objects, ascending z (stable reference until something changes). */
  listPage(pageId: string): ReadonlyArray<Readonly<ObjectData>> {
    let out = this.byPage.get(pageId);
    if (!out) {
      const pages = this.pageMembership();
      out = this.listAll().filter((o) => pages.get(o.id) === pageId);
      this.byPage.set(pageId, out);
    }
    return out;
  }

  /** The page an object is shown on (P14 Ruling 5), undefined for an unknown id. */
  pageOf(id: string): string | undefined {
    return this.pageMembership().get(id);
  }

  private pageMembership(): Map<string, string> {
    this.assignment ??= pageAssignment(this.cache as ReadonlyMap<string, ObjectData>, this.pages());
    return this.assignment;
  }

  /** The board's pages in display order, never empty (stable reference until they change). */
  pages(): readonly PageEntry[] {
    this.pagesCache ??= Object.freeze(readPages(this.doc));
    return this.pagesCache;
  }

  /**
   * Which objects are on which page changed (an object added, removed, moved to another page or parent, or the pages
   * themselves): never on a move or an edit, so a layer that only sorts objects by page skips drag frames.
   */
  subscribeMembership(cb: Listener): () => void {
    this.membershipSubs.add(cb);
    return () => void this.membershipSubs.delete(cb);
  }

  subscribePages(cb: Listener): () => void {
    this.pagesSubs.add(cb);
    return () => void this.pagesSubs.delete(cb);
  }

  /** The active page's id. */
  get page(): string {
    return this.activePage;
  }

  /** Shows another page (an unknown id shows the first): list subscribers are told, nothing is written. */
  setPage(id: string): void {
    const next = this.pages().some((p) => p.id === id) ? id : this.pages()[0]!.id;
    if (next === this.activePage) return;
    this.activePage = next;
    this.pageSubs.forEach((cb) => cb());
    this.listSubs.forEach((cb) => cb());
  }

  subscribePage(cb: Listener): () => void {
    this.pageSubs.add(cb);
    return () => void this.pageSubs.delete(cb);
  }

  /** Board settings (background, grid), defaults filled in (stable reference until they change). */
  meta(): BoardMeta {
    this.metaCache ??= readMeta(this.doc.getMap('meta'));
    return this.metaCache;
  }

  subscribeMeta(cb: Listener): () => void {
    this.metaSubs.add(cb);
    return () => void this.metaSubs.delete(cb);
  }

  /** The active page's parents, world boxes and paint order (stable reference until something changes). */
  graph(): SceneGraph {
    return this.graphOf(this.activePage);
  }

  graphOf(pageId: string): SceneGraph {
    let g = this.graphs.get(pageId);
    if (!g) this.graphs.set(pageId, (g = buildGraph(this.listPage(pageId))));
    return g;
  }

  subscribe(id: string, cb: Listener): () => void {
    let set = this.idSubs.get(id);
    if (!set) this.idSubs.set(id, (set = new Set()));
    set.add(cb);
    return () => void set.delete(cb);
  }

  subscribeList(cb: Listener): () => void {
    this.listSubs.add(cb);
    return () => void this.listSubs.delete(cb);
  }

  /** Told when a local command is refused because the board is full (P12a). */
  subscribeLimit(cb: (kind: LimitKind) => void): () => void {
    this.limitSubs.add(cb);
    return () => void this.limitSubs.delete(cb);
  }

  /** Objects on every page and images among them, as the board limits count them. */
  counts(): BoardCounts {
    return boardCounts(this.objects);
  }

  subscribeHistory(cb: Listener): () => void {
    this.histSubs.add(cb);
    return () => void this.histSubs.delete(cb);
  }

  /** True while the user may not edit (viewer, or a role lowered by the server): commands, undo and redo do nothing. */
  get readOnly(): boolean {
    return this.readOnlyValue;
  }

  /** Who adds objects from now on (P5d): the signed-in user, known once the board opens; null in local mode. */
  setAuthor(author: (() => string | null) | null): void {
    this.author = author;
  }

  setReadOnly(v: boolean): void {
    if (v === this.readOnlyValue) return;
    this.readOnlyValue = v;
    this.readOnlySubs.forEach((cb) => cb());
  }

  subscribeReadOnly(cb: Listener): () => void {
    this.readOnlySubs.add(cb);
    return () => void this.readOnlySubs.delete(cb);
  }

  /**
   * Opens a gesture (drag, resize, rotate): every local write until `endGesture` is ONE undo step, however long
   * the pointer rests mid-gesture.
   */
  beginGesture(): void {
    if (this.gesture) this.endGesture();
    this.history.stopCapturing();
    this.gesture = { depth: this.history.undoStack.length, captureTimeout: this.history.captureTimeout };
    this.history.captureTimeout = Number.POSITIVE_INFINITY;
  }

  endGesture(): void {
    if (!this.gesture) return;
    this.history.captureTimeout = this.gesture.captureTimeout;
    this.gesture = null;
    this.history.stopCapturing();
  }

  /** Reverts what the open gesture wrote and closes it, leaving neither an undo nor a redo step. */
  cancelGesture(): void {
    const g = this.gesture;
    if (!g) return;
    this.endGesture();
    if (this.history.undoStack.length > g.depth) {
      this.history.undo();
      this.history.redoStack.pop();
      this.histSubs.forEach((cb) => cb());
    }
  }

  undo(): void {
    if (!this.readOnlyValue) this.history.undo();
  }

  redo(): void {
    if (!this.readOnlyValue) this.history.redo();
  }

  get canUndo(): boolean {
    return this.history.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.history.redoStack.length > 0;
  }
}
